import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../Theme';

const LibraryContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.textPrimary};
  margin: 0 auto;
  align-content: space-around;
  justify-content: flex-start;
  flex-wrap: wrap;
  position: relative;
  height: 100vh;
`;

const MainSection = styled.div`
  max-width: 800px;
  text-align: justify;
  margin-bottom: 20px;
  max-height: 70vh;
`;

const Title = styled.h2`
  color: ${theme.colors.primary};
  font-family: ${theme.fonts.main};
  font-size: 2.5rem;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const Subtitle = styled.h3`
  color: ${theme.colors.accent};
  font-family: Arial, sans-serif;
  font-size: 1.5rem;
  margin-bottom: 15px;
`;

const Description = styled.div`
  font-family: Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.6;
  color: ${theme.colors.textSecondary};
  margin-bottom: 20px;
`;

const StackButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: 'rgb(8 13 33 / 25%)';
  color: ${theme.colors.secondary};
  padding: 10px 20px;
  border: none;
  border-radius: 33px;
  cursor: pointer;
  font-size: 1rem;
  z-index: 1000;

  &:hover {
    background-color: ${theme.colors.accent};
  }
`;

const SupportButton = styled.button`
  background-color: ${theme.colors.primary};
  color: ${theme.colors.secondary};
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  z-index: 1000;
  &:hover {
    background-color: ${theme.colors.accent};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: ${theme.colors.secondary};
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 300px;
  font-size: 1rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
  position: relative;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 10px;
`;

const Library = () => {
  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeModal();
    }
  };

  const openSupportLink = () => {
    window.open('https://shinedarkmusic.com/', '_blank');
  };

  return (
    <LibraryContainer>
      <StackButton onClick={openModal}>Stack</StackButton>
      {isModalOpen && (
        <ModalOverlay onClick={handleOverlayClick}>
          <ModalContent>
            <Title>Tech Stack</Title>
            <Description>
              Planetaria Radio is built with a modern tech stack, including:
              <List>
                <ListItem><strong>React:</strong> Front-end library for building user interfaces.</ListItem>
                <ListItem><strong>GraphQL & Apollo Client:</strong> For querying the backend and managing state.</ListItem>
                <ListItem><strong>Polygon:</strong> For managing blockchain with tokengate NFT's.</ListItem>
                <ListItem><strong>Styled Components:</strong> For styling the application.</ListItem>
                <ListItem><strong>Pinata & IPFS:</strong> For decentralized file storage.</ListItem>
                <ListItem><strong>Remix:</strong> To deploy contract.</ListItem>
                <ListItem><strong>Ethereum Sepolia:</strong> To interact with the contract.</ListItem>
                <ListItem><strong>Solidity:</strong> For writing smart contracts on the Polygon blockchain.</ListItem>
                <ListItem><strong>Hardhat:</strong> Development environment for compiling, testing, and deploying smart contracts on Polygon.</ListItem>
              </List>
            </Description>
          </ModalContent>
        </ModalOverlay>
      )}

      <MainSection>
        <Title>Welcome to PLANETARIA RADIO</Title>
        <SupportButton onClick={openSupportLink}>Support</SupportButton>
        <Subtitle>Overview</Subtitle>
        <Description>
          Planetaria Radio is a unique platform that combines music streaming with blockchain technology. Here's how it works:
          <List>
            <ListItem>Users can stream music tracks. If you have a specific polygon nft in your wallet you can upload.</ListItem>
            <ListItem>Each track is stored as an encrypted file on IPFS, ensuring decentralized and secure storage.</ListItem>
            <ListItem>Track metadata and ownership are managed through smart contracts on Sepolia blockchain.</ListItem>
            <ListItem>When a user plays a track, the play event is logged on the blockchain.</ListItem>
            <ListItem>Users can view play counts and last played timestamps for each track.</ListItem>
          </List>
          Key Features:
          <List>
            <ListItem>Create and upload music tracks with associated metadata.</ListItem>
            <ListItem>Play and stream music directly from the platform.</ListItem>
            <ListItem>Track play counts and engagement using blockchain technology.</ListItem>
            <ListItem>Decentralized file storage ensures longevity and resistance to censorship.</ListItem>
            <ListItem>NFT integration allows for unique ownership and potential monetization of tracks.</ListItem>
          </List>
          Planetaria Radio combines the best of Web3 technology with a user-friendly music streaming experience, creating a next-generation platform for artists and listeners alike.
        </Description>
      </MainSection>
    </LibraryContainer>
  );
};

export default Library;